import {
	type NatsConnection,
	StringCodec,
	type Subscription,
	wsconnect
} from '@nats-io/nats-core'
import { EVENT_TYPES, type EventMessage, type ForEventsMessaging } from 'core'

export type NatsConnectionOptions = {
	servers: string | string[]
	pingInterval: number
}

export type EnvironmentReadyDataDTO = {
	env_id: string
	timestamp: string
}

export type EventMessageDataTypesDTO = EnvironmentReadyDataDTO

export class NatsMessagingAdapter implements ForEventsMessaging {
	private nc?: NatsConnection
	private sc = StringCodec()
	private subscriptions: Map<string, Subscription> = new Map()
	private readonly _options: NatsConnectionOptions

	constructor(options: NatsConnectionOptions) {
		this._options = options
	}

	async connect(): Promise<void> {
		try {
			this.nc = await wsconnect(this._options)
			console.log('Connected to NATS server')
		} catch (error) {
			console.error('Failed to connect to NATS server:', error)
			throw error
		}
	}

	subscribe(subject: string, callback: (message: EventMessage) => void): void {
		if (!this.nc) {
			throw new Error('NATS connection not established')
		}

		if (this.nc.isClosed()) {
			throw new Error('NATS connection has been closed')
		}

		if (this.subscriptions.has(subject)) {
			throw new Error(`Already subscribed to subject: ${subject}`)
		}

		const sub = this.nc.subscribe(subject)
		this.subscriptions.set(subject, sub)
		;(async () => {
			try {
				for await (const msg of sub) {
					const data = this.sc.decode(msg.data)
					const dataObject = JSON.parse(data)
					const formattedMsgData = this.formatMsgData(dataObject)

					callback({
						data: formattedMsgData,
						subject: msg.subject,
						type: dataObject.type
					})
				}
			} catch (err) {
				console.error(`Subscription error on subject ${subject}:`, err)
			}
		})()
	}

	async disconnect(): Promise<void> {
		if (this.nc) {
			const nc = this.nc // Capture the current connection
			this.nc = undefined // Prevent further use

			for (const [subject, sub] of this.subscriptions) {
				sub.unsubscribe()
				this.subscriptions.delete(subject)
			}

			await nc.close()

			// Wait for the connection to close and check for errors
			const err = await nc.closed()
			if (err) {
				console.error('Error closing NATS connection:', err)
			} else {
				console.log('Disconnected from NATS server')
			}
		}
	}

	private formatMsgData = (data: Record<string, unknown>) => {
		switch (data['type']) {
			case EVENT_TYPES.EnvironmentReady: {
				return {
					workspaceURL: `/ide/${data['env_id']}`,
					timestamp: data['timestamp'] as string
				}
			}
			case EVENT_TYPES.PaymentCompleted: {
				return {
					id: data['id'] as string,
					type: data['type'] as string,
					subscriptionId: data['subscription_id'] as string,
					paymentId: data['payment_id'] as string,
					paymentStatus: data['payment_status'] as 'paid' | 'failed',
					createdAt: data['created_at'] as string
				}
			}
			default:
				return data
		}
	}
}
